/* import __COLOCATED_TEMPLATE__ from './insurance-card.hbs'; */
import { UploadFile } from 'ember-file-upload';
import { action } from '@ember/object';
import { confirmation } from 'client-portal/utils/modals';
import { createElement, success, warning } from 'client-portal/utils/banners';
import { isImage } from 'client-portal/utils/file';
import { or, reads } from 'macro-decorators';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import generateUUID from 'ember-simplepractice/utils/generate-uuid';

const REQUEST_TIMEOUT_MESSAGE = 'Request timeout';
const TIMEOUT = 10000;
const TIMEOUT_ANIMATION_INTERVAL = 1000;
const AMOUNT_TO_FILL_EACH_INTERVAL = 1 / (TIMEOUT / TIMEOUT_ANIMATION_INTERVAL);
export default class SharedInsuranceCard extends Component {
  @service analytics;
  @service clientInsuranceAccess;
  @service currentPractice;
  @service fileUpload;
  @service intl;
  @service media;
  @service store;

  @tracked displayLoadingModal;
  @tracked isMarkedForDeletion = this.args.card?.isMarkedForDeletion;
  @tracked intervalId;
  @tracked textExtractionProgress;

  @reads('args.card') card;
  @reads('card.id') cardId;
  @reads('card.isNew') cardIsNew;
  @reads('card.documentExt') documentExt;
  @reads('args.insuranceInfoRequestConnection') insuranceInfoRequestConnection;
  @or('insuranceInfoRequestConnection', 'insuranceInfo') insuranceInfoObject;
  @reads('args.side') side;
  @reads('card.file.file.type') type;

  get insuranceInfo() {
    return this.args.insuranceInfo || this.args.client?.primaryInsuranceInfo;
  }

  get isImage() {
    return isImage(this);
  }

  get fileUrl() {
    if (this.cardId && !this.isMarkedForDeletion) {
      let adapter = this.store.adapterFor('insurance-card');
      return adapter.urlForFindRecord(this.cardId, 'insurance-card', {});
    } else if (this.cardIsNew) {
      return URL.createObjectURL(this.card.file.file);
    } else {
      return undefined;
    }
  }

  @action
  removeAssociation() {
    return this.cardIsNew ? this.card.deleteRecord() : this.card.set('isMarkedForDeletion', true);
  }

  @action
  async uploadCard(file) {
    let card = await this.store.createRecord('insurance-card', {
      file,
      documentMimeType: file.type,
      insuranceInfo: this.insuranceInfo,
      insuranceInfoRequestConnection: this.insuranceInfoRequestConnection,
    });

    if (this.insuranceInfoRequestConnection) {
      this.analytics.track('Client Insurance Info Submission Insurance Card Uploaded', {
        object: 'Client Insurance Info Submission Insurance Card',
        action: 'Uploaded',
      });
    }

    this.insuranceInfoObject.documents.pushObject(card);

    if (this.clientInsuranceAccess.claimAssistantOrManagedBilling && !this.args?.textExtraction) {
      await this._startTextExtraction(card);
    }
  }

  async _extractText(card) {
    try {
      let adapter = this.store.adapterFor('insurance-text-extraction');
      let data = card.serialize({}).data;
      let blob = card.file.file;
      let fileForTextExtraction = new UploadFile(new File([blob], blob.name), { type: blob.type });
      let result = await this._requestWithTimeout(
        adapter.extractInsuranceCardInfoFromFile(fileForTextExtraction, data),
        TIMEOUT
      );

      result.body.data.id = generateUUID();
      card.setProperties({
        insuranceTextExtractionDraftId: result.body.data.id,
      });
      this.store.pushPayload(result.body);

      let insuranceTextExtraction = this.store.peekRecord(
        'insurance-text-extraction',
        result.body.data.id
      );
      this.insuranceInfoObject.assignMappedFields(insuranceTextExtraction);

      this._fieldsFilled();
    } catch (error) {
      this._handleExtractionError();
    } finally {
      this.intervalId && clearInterval(this.intervalId);
      this.displayLoadingModal = false;
      this.textExtractionProgress = 0;
    }
  }

  _fieldsFilled() {
    if (this.insuranceInfoObject.extractionFieldsPresent) {
      success({ title: this.intl.t('messages.success.insuranceInfoAdded') });
    } else {
      warning({
        title: this.intl.t('messages.error.cannotAutofillInsuranceInfo'),
        content: createElement('p', {
          innerText: `Information couldn't be filled from the insurance card`,
        }),
      });
    }
  }

  _handleExtractionError() {
    warning({
      title: this.intl.t('messages.error.cannotAutofillInsuranceInfo'),
      content: createElement('p', {
        innerText: `Information couldn't be filled from the insurance card`,
      }),
    });
  }

  _promiseTimeout(timeout) {
    return new Promise((_, reject) => {
      // eslint-disable-next-line ember-cleanup/no-settimeout
      setTimeout(() => {
        reject(new Error(REQUEST_TIMEOUT_MESSAGE));
      }, timeout);
    });
  }

  _requestWithTimeout(request, timeout) {
    return Promise.race([request, this._promiseTimeout(timeout)]);
  }

  async _startTextExtraction(card) {
    if (this.insuranceInfoObject?.extractionFieldsPresent) {
      let { dismiss } = await confirmation({
        title: this.intl.t('messages.insuranceCard.confirmUpdateTitle'),
        text: 'Existing info will be deleted and replaced with info from the new images',
        confirmButtonText: 'Update info',
        cancelButtonText: 'Save without updating',
      });

      if (dismiss) return;
    }

    this.displayLoadingModal = true;
    this.textExtractionProgress = 0;
    this._startTextExtractionProgressAnimation();
    await this._extractText(card);
  }

  _startTextExtractionProgressAnimation() {
    this.intervalId = setInterval(() => {
      if (this.textExtractionProgress + AMOUNT_TO_FILL_EACH_INTERVAL < 1) {
        this.textExtractionProgress += AMOUNT_TO_FILL_EACH_INTERVAL;
      }
    }, TIMEOUT_ANIMATION_INTERVAL);
  }
}
